import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListingApiClient } from 'api/listing';
import { ListingStatus } from 'models/Listing/types';
import { PaginatedListingGrid, Modal, GroupingCreationWizard, ListingTagWizard } from 'components';
import { NavigationService } from 'services';
import { ListingModel, ItemModel } from 'models';
import { ListingTagType } from 'components/Wizard/ListingTagWizard/ListingTagWizard';
import { useCreateGrouping } from 'hooks/queries/useGroupings';
import { useMarkListingAsFake, useMarkListingAsOriginal, useMarkListingAsSet } from 'hooks/queries/useListings';
import { JSONObject } from 'models/Api/types';

export default function DashboardPendingListingsCard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [linkListing, setLinkListing] = useState<ListingModel | null>(null);
  const [tagListing, setTagListing] = useState<ListingModel | null>(null);

  const { data: pendingListings, isLoading } = useQuery({
    queryKey: ['dashboard-pending-listings', page],
    queryFn: () =>
      ListingApiClient.searchListings({
        page,
        processing_status: [ListingStatus.PENDING],
        sort_by: 'date',
        sort_type: 'desc',
      }),
  });

  const createGroupingMutation = useCreateGrouping({ navigateOnSuccess: false });
  const markAsFakeMutation = useMarkListingAsFake();
  const markAsOriginalMutation = useMarkListingAsOriginal();
  const markAsSetMutation = useMarkListingAsSet();

  const invalidatePendingListings = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });
  };

  const handleListingNameClick = (listingId: string, listingName: string) => {
    navigate(NavigationService.getListingDetailsPath(listingId, listingName));
  };

  const handleListingItemClick = (itemId: string, itemName: string) => {
    navigate(NavigationService.getItemDetailsPath(itemId, itemName));
  };

  const handleListingArtistClick = (artistId: string, artistName: string) => {
    navigate(NavigationService.getArtistDetailsPath(artistId, artistName));
  };

  const handleItemSuggestionClick = useCallback((listing: ListingModel) => {
    createGroupingMutation.reset();
    setLinkListing(listing);
  }, [createGroupingMutation]);

  const handleTagListingClick = useCallback((listing: ListingModel) => {
    markAsFakeMutation.reset();
    markAsOriginalMutation.reset();
    markAsSetMutation.reset();
    setTagListing(listing);
  }, [markAsFakeMutation, markAsOriginalMutation, markAsSetMutation]);

  const handleLinkListingSubmit = useCallback((item: ItemModel, listings: ListingModel[]) => {
    if (!listings.length) {
      return;
    }

    createGroupingMutation.mutate(
      {
        item_id: item.id,
        listings: listings.map((listing) => listing.id),
        auto_link: true
      },
      {
        onSuccess: () => {
          invalidatePendingListings();
          setLinkListing(null);
        },
      }
    );
  }, [createGroupingMutation]);

  const handleTagListingSubmit = useCallback((listings: ListingModel[], tagType: ListingTagType, data: JSONObject) => {
    const listingIds = listings.map(l => l.id);
    const onSuccess = () => {
      invalidatePendingListings();
      setTagListing(null);
    };

    if (tagType === 'FAKE') {
      markAsFakeMutation.mutate(listingIds, { onSuccess });
    } else if (tagType === 'SET') {
      markAsSetMutation.mutate(listingIds, { onSuccess });
    } else if (tagType === 'ORIGINAL') {
      markAsOriginalMutation.mutate(listingIds, { onSuccess });
    }
  }, [markAsFakeMutation, markAsSetMutation, markAsOriginalMutation]);

  const tagMutationPending =
    markAsFakeMutation.isPending ||
    markAsOriginalMutation.isPending ||
    markAsSetMutation.isPending;
  const tagMutationErrored =
    markAsFakeMutation.isError ||
    markAsOriginalMutation.isError ||
    markAsSetMutation.isError;

  return (
    <>
      <Card width="100%">
        <CardBody>
          <PaginatedListingGrid
            paginatedListings={pendingListings}
            onPageSelect={setPage}
            scrollMode={false}
            onListingNameClick={handleListingNameClick}
            onListingItemClick={handleListingItemClick}
            onListingArtistClick={handleListingArtistClick}
            isLoading={isLoading}
            showFilters={false}
            compactMode={true}
            showAssociationAction={true}
            onItemSuggestionClick={handleItemSuggestionClick}
            onTagListingClick={handleTagListingClick}
            itemSuggestionLabel="Link Item"
            contentMaxHeight="1200px"
          />
        </CardBody>
      </Card>
      <Modal
        isOpen={!!linkListing}
        onClose={() => setLinkListing(null)}
        title="Link Item"
        content={
          linkListing ? (
            <GroupingCreationWizard
              defaultArtistId={linkListing.artistId}
              defaultListings={[linkListing]}
              defaultItem={linkListing.getAssociatedItem() || null}
              preselectedListings={[linkListing]}
              initialWorkflow="listing-first"
              onSubmit={handleLinkListingSubmit}
              isLoading={createGroupingMutation.isPending}
            />
          ) : null
        }
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
      <Modal
        isOpen={!!tagListing}
        onClose={() => setTagListing(null)}
        title="Tag Sale"
        content={
          tagListing ? (
            <ListingTagWizard
              listings={[tagListing]}
              preselectedListings={[tagListing]}
              onSubmit={handleTagListingSubmit}
              isLoading={tagMutationPending}
            />
          ) : null
        }
        showCloseButton={false}
        showSubmitButton={false}
        size="2xl"
      />
    </>
  );
}
